// Main ContractOverview.tsx (Complete version with payment success handling)
import React, { useState, useCallback, useEffect } from 'react';
import { Alert } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { ContractTemplateProps, Milestone, ProposedDates } from './Contract.interface';
import { useUser } from '../../hooks';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { useMilestoneEnrichment } from './useMilestoneEnrichment';
import { ContractService } from './ContractService';
import ContractTimeline from './ContractTimeline';
import InspectionManager from './InspectionManager';
import ModalManager from './ModalManager';

interface ContractOverviewProps {
  contract: ContractTemplateProps;
  onNavigateToTab?: (tabIndex: number) => void;
}

const ContractOverview: React.FC<ContractOverviewProps> = ({ contract, onNavigateToTab }) => {
  const { currentUserId, isContractor } = useUser();
  const { formatProposedDate } = useDateFormatter();
  const location = useLocation();

  // State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [proposedDates, setProposedDates] = useState<ProposedDates | null>(null);
  const [selectedInspectionDate, setSelectedInspectionDate] = useState<string | null>(null);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [isPostInspectionModalOpen, setIsPostInspectionModalOpen] = useState(false);
  const [isHomeownerReviewModalOpen, setIsHomeownerReviewModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentMilestone, setSelectedPaymentMilestone] = useState<{
    id: string;
    title: string;
    amount: number;
  } | null>(null);

  // Payment success state
  const [paymentSuccessAlert, setPaymentSuccessAlert] = useState<{
    show: boolean;
    milestoneTitle: string;
    amount: number;
  }>({ show: false, milestoneTitle: '', amount: 0 });

  // Check for payment success in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const milestoneId = urlParams.get('milestone_id');

    if (paymentSuccess === 'true' && milestoneId) {
      // Find the milestone that was paid
      const paidMilestone = milestones.find(m => m.id === milestoneId);
      if (paidMilestone) {
        setPaymentSuccessAlert({
          show: true,
          milestoneTitle: paidMilestone.title,
          amount: paidMilestone.amount || 0
        });

        // Auto-hide the alert after 10 seconds
        setTimeout(() => {
          setPaymentSuccessAlert(prev => ({ ...prev, show: false }));
        }, 10000);
      }

      // Clean up URL parameters
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search, milestones]);

  // Action handlers
  const handlePaymentAction = useCallback((milestoneId: string, amount: number) => {
    const milestone = milestones.find(m => m.id === milestoneId);
    if (milestone) {
      setSelectedPaymentMilestone({
        id: milestoneId,
        title: milestone.title,
        amount: amount
      });
      setIsPaymentModalOpen(true);
    }
  }, [milestones]);

  const handlePaymentSuccess = useCallback(() => {
    if (selectedPaymentMilestone) {
      const now = new Date().toISOString();

      const updatedMilestones = milestones.map(milestone => {
        if (milestone.id === selectedPaymentMilestone.id) {
          return {
            ...milestone,
            status: 'paid' as const,
            date: now, // Update the date to current time
            paymentStatus: 'processing',
            paymentReleased: true,
            action: undefined
          };
        }
        return milestone;
      });

      setMilestones(updatedMilestones);
      setIsPaymentModalOpen(false);

      // Show success alert
      setPaymentSuccessAlert({
        show: true,
        milestoneTitle: selectedPaymentMilestone.title,
        amount: selectedPaymentMilestone.amount
      });

      // Auto-hide after 10 seconds
      setTimeout(() => {
        setPaymentSuccessAlert(prev => ({ ...prev, show: false }));
      }, 10000);

      setSelectedPaymentMilestone(null);
    }
  }, [milestones, selectedPaymentMilestone]);

  // Milestone enrichment
  const { enrichMilestone } = useMilestoneEnrichment({
    currentUserId: currentUserId || '', // Ensure it's always a string
    isContractor,
    contract,
    onNavigateToTab,
    onSetInspectionDates: () => setIsModalOpen(true),
    onPostInspectionAction: () => setIsPostInspectionModalOpen(true),
    onHomeownerReview: () => setIsHomeownerReviewModalOpen(true),
    onPaymentAction: handlePaymentAction
  });

  // Contract service handlers
  const handleInspectionDatesSet = useCallback(async (dates: ProposedDates) => {
    try {
      const updatedMilestones = await ContractService.updateInspectionDates(
        contract.id,
        contract.milestones,
        dates,
        formatProposedDate
      );

      setMilestones(updatedMilestones);
      setProposedDates(dates);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating inspection dates:', error);
    }
  }, [contract.id, contract.milestones, formatProposedDate]);

  const handleInspectionDateSelected = useCallback(async (selectedDate: string) => {
    try {
      const updatedMilestones = await ContractService.updateSelectedInspectionDate(
        contract.id,
        contract.milestones,
        selectedDate,
        isContractor,
        () => setIsPostInspectionModalOpen(true)
      );

      setMilestones(updatedMilestones);
      setSelectedInspectionDate(selectedDate);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating selected inspection date:', error);
    }
  }, [contract.id, contract.milestones, isContractor]);

  // Initialize milestones
  useEffect(() => {
    if (!contract.id || !contract.milestones) return;

    const setupMilestone = contract.milestones.find(m => m.id === 'inspection-setup');
    const dateSelectionMilestone = contract.milestones.find(m => m.id === 'inspection-date-selection');

    const extractedProposedDates = setupMilestone?.proposedDates as ProposedDates | null;
    const extractedSelectedDate = dateSelectionMilestone?.selectedDate ?
      formatProposedDate(dateSelectionMilestone.selectedDate) : null;

    const enrichedMilestones = contract.milestones.map(milestone =>
      enrichMilestone(milestone, extractedProposedDates, extractedSelectedDate)
    );

    setMilestones(enrichedMilestones);
    setProposedDates(extractedProposedDates);
    setSelectedInspectionDate(extractedSelectedDate);
  }, [
    contract.id,
    contract.milestones,
    contract.ownerSignature,
    contract.contractorSignature,
    contract.ownerSignatureDate,
    contract.contractorSignatureDate,
    enrichMilestone,
    formatProposedDate
  ]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Payment Success Alert */}
      {paymentSuccessAlert.show && (
        <div className="mb-4">
          <Alert
            color="success"
            isOpen={paymentSuccessAlert.show}
            toggle={() => setPaymentSuccessAlert(prev => ({ ...prev, show: false }))}
            className="alert-dismissible"
          >
            <strong>Payment Successful!</strong><br />
            Your payment of {formatCurrency(paymentSuccessAlert.amount)} for milestone
            "{paymentSuccessAlert.milestoneTitle}" has been processed successfully.
            The milestone is now marked as paid and your ACH transfer will complete within 1-3 business days.
          </Alert>
        </div>
      )}

      <InspectionManager
        milestones={milestones}
        maxInspectionDays={contract.inspectionDays}
        isModalOpen={isModalOpen}
        toggleModal={() => setIsModalOpen(!isModalOpen)}
        onDatesSet={handleInspectionDatesSet}
        onDateSelected={handleInspectionDateSelected}
        proposedDates={proposedDates}
        selectedDate={selectedInspectionDate}
        isContractor={isContractor}
      />

      <ContractTimeline milestones={milestones} />

      <ModalManager
        contract={contract}
        isPostInspectionModalOpen={isPostInspectionModalOpen}
        setIsPostInspectionModalOpen={setIsPostInspectionModalOpen}
        isHomeownerReviewModalOpen={isHomeownerReviewModalOpen}
        setIsHomeownerReviewModalOpen={setIsHomeownerReviewModalOpen}
        isPaymentModalOpen={isPaymentModalOpen}
        setIsPaymentModalOpen={setIsPaymentModalOpen}
        selectedPaymentMilestone={selectedPaymentMilestone}
        setSelectedPaymentMilestone={setSelectedPaymentMilestone}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default ContractOverview;